import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faPlus } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import "./formBuilderLibrary.scss";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import {
  NavigationDropdownNew,
} from "components/navigationDropdownNew/NavigationDropdownNew";
import {
  websiteBuilderAddForm,
  websiteBuilderArchiveForm,
  websiteBuilderUpdateFormName,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IFormBuilderForm } from "../editor/model/formBuilderForm";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_PORTAL } from "../../WebsiteBuilderEditor";
import { formBuilderSanitizeForm } from "../transforms/sanitize/formBuilderSanitizeForm";

interface IFormBuilderLibraryProps {
  selectedFormId: number;
  setSelectedFormId: (formId: number) => void;
}

interface IFormBuilderLibraryRenameFormModalState {
  id: number;
  prevName: string;
  nextName: string;
}

interface IFormBuilderLibraryArchiveFormModalState {
  id: number;
}

export function FormBuilderLibrary(props: IFormBuilderLibraryProps) {
  const { selectedFormId, setSelectedFormId } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const forms = useAppSelector(store => store.websiteBuilderStore.editor.forms);

  const [searchQuery, setSearchQuery] = useState<string>("");

  const {
    state: renameFormModalState,
    closeModal: closeRenameFormModal,
    updateModal: updateRenameFormModal,
  } = useModal<IFormBuilderLibraryRenameFormModalState>({
    id: null,
    prevName: "",
    nextName: "",
  });

  const {
    state: archiveFormModalState,
    closeModal: closeArchiveFormModal,
    updateModal: updateArchiveFormModal,
  } = useModal<IFormBuilderLibraryArchiveFormModalState>({
    id: null,
  });

  const filteredForms = useMemo(
    () =>
      forms.filter(form =>
        form.settings.name != null &&
        form.settings.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
      ),
    [forms, searchQuery],
  );

  function duplicateForm(form: IFormBuilderForm) {
    const sanitizedForm = formBuilderSanitizeForm(form);

    sanitizedForm.settings.name += ` - ${t("Duplicate")}`;

    void dispatch(websiteBuilderAddForm(sanitizedForm, t));
  }

  function renameForm(id: number, name: string) {
    void dispatch(
      websiteBuilderUpdateFormName(id, name, t, () => {
        closeRenameFormModal();
      }),
    );
  }

  function archiveForm(id: number) {
    void dispatch(
      websiteBuilderArchiveForm(id, t, () => {
        if (selectedFormId === id) {
          setSelectedFormId(null);
        }

        closeArchiveFormModal();
      }),
    );
  }

  function openRenameFormModal(form: IFormBuilderForm) {
    updateRenameFormModal({
      isOpen: true,
      id: form.id,
      prevName: form.settings.name,
      nextName: form.settings.name,
    });
  }

  function openArchiveFormModal(form: IFormBuilderForm) {
    updateArchiveFormModal({
      isOpen: true,
      id: form.id,
    });
  }


  return (
    <div className="form-builder-library">
      <Input
        labelHidden
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        onSearchClear={() => setSearchQuery("")}
        placeholder={t("Search for your form...")}
      />

      <div className="library">
        {filteredForms.map(form => (
          <div
            key={form.id}
            className={classNames("library-card", {
              "library-card-selected": selectedFormId === form.id,
            })}
            onClick={() => setSelectedFormId(form.id)}
          >
            <div className="library-card-form">
              <div className="library-card-form-details">
                <div className="library-card-form-name">{form.settings.name}</div>
                <div className="library-card-form-updated-at">
                  {form.updatedAt != null
                    ? t("Edited on {{time}}", { time: moment(form.updatedAt).format("MMM D, YYYY") })
                    : ""}
                </div>
              </div>

              <div className="library-card-form-options">
                <NavigationDropdownNew
                  hideChevron
                  showPlainTextLabel
                  label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                  rightAlign
                  sections={[
                    [
                      {
                        label: t("Duplicate"),
                        icon: "layer-group",
                        onClick: () => duplicateForm(form),
                      },
                      {
                        label: t("Rename"),
                        icon: "text",
                        onClick: () => openRenameFormModal(form),
                      },
                      {
                        label: t("Archive"),
                        icon: "box-archive",
                        onClick: () => openArchiveFormModal(form),
                      },
                    ],
                  ]}
                />
              </div>
            </div>
          </div>
        ))}

        {searchQuery.length === 0 && (
          <div
            className={classNames("library-card", {
              "library-card-selected": selectedFormId == null,
            })}
            onClick={() => setSelectedFormId(null)}
          >
            <div className="library-card-new-form">
              <FontAwesomeIcon className="library-card-new-form-icon" icon={faPlus} />
              <div className="library-card-new-form-message">{t("New form")}</div>
            </div>
          </div>
        )}
      </div>

      <Portal isMounted={renameFormModalState.isOpen} parentId={WEBSITE_BUILDER_PORTAL}>
        <Sheet
          open={renameFormModalState.isOpen}
          title={t("Rename form")}
          supportText={t("Edit your form name here.")}
          size="small"
          stacked
          closable
          onOk={() => renameForm(renameFormModalState.id, renameFormModalState.nextName)}
          okText={t("Save")}
          okDisabled={
            renameFormModalState.nextName.length === 0 ||
            renameFormModalState.nextName === renameFormModalState.prevName
          }
          onCancel={closeRenameFormModal}
        >
          <Input
            label={t("Form name")}
            value={renameFormModalState.nextName}
            onChange={e => updateRenameFormModal({ nextName: e.target.value })}
          />
        </Sheet>
      </Portal>

      <Portal isMounted={archiveFormModalState.isOpen} parentId={WEBSITE_BUILDER_PORTAL}>
        <Sheet
          open={archiveFormModalState.isOpen}
          title={t("Archive form")}
          size="small"
          stacked
          closable
          onOk={() => archiveForm(archiveFormModalState.id)}
          okText={t("Archive")}
          onCancel={closeArchiveFormModal}
        >
          <div>{t("Are you sure you want to archive this form? You can still view its submissions after it's archived.")}</div>
        </Sheet>
      </Portal>
    </div>
  );
}
